<template>
  <div
    :key="pushindex"
    class="component-upload-image"
  >
    <input
      ref="fileInputRef"
      type="file"
      multiple
      :accept="fileAccept"
      class="file-input"
      @change="handleFileChange"
    >
    <el-button
      type="primary"
      @click="triggerFileInput"
    >
      <svg-icon
        icon-class="materialUpload"
        style="margin: 0 5px"
      />{{ proxy.$t('material.UploadMaterial') }}
    </el-button>

    <el-dialog
      v-model="preOpen"
      :title="proxy.$t('material.UploadMaterial')"
      width="450px"
      append-to-body
    >
      <div class="PreCenter">
        <el-icon
          v-if="successCount > 0 && failCount > 0"
          :size="24"
          color="#ffa200"
        >
          <WarningFilled />
        </el-icon>

        <el-icon
          v-else-if="failCount > 0"
          :size="24"
          color="#e1291e"
        >
          <CircleCloseFilled />
        </el-icon>
        <el-icon
          v-else
          :size="26"
          color="#00c261"
        >
          <SuccessFilled />
        </el-icon>

        <div class="pretip">
          {{
            successCount > 0 && failCount > 0
              ? proxy.$t('material.sFail')
              : successCount > 0
                ? proxy.$t('material.uploadSuccess')
                : proxy.$t('material.uploadFail')
          }}
        </div>
        <div class="perfill">
          <span class="peritem">{{ proxy.$t('material.uploadSuccess') }}:<span class="successCount">{{ successCount }}</span></span>
          <span
            v-show="failCount > 0"
            class="peritem"
          >{{ proxy.$t('material.uploadFail') }}:<span class="failCount">{{ failCount }}</span></span>
          <span
            v-show="failCount > 0"
            class="peritem active"
            @click="failDetail"
          >{{ proxy.$t('excel.failDetail') }}</span>
        </div>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <!-- 添加 loading 状态 -->

          <el-button
            type="primary"
            link
            @click="preOpen = false"
          >
            {{ proxy.$t('Shared.Confirm') }}
          </el-button>
        </div>
      </template>
      <el-drawer
        v-model="drawer"
        :with-header="false"
        :before-close="handleClose"
      >
        <div class="title">
          导入失败数据({{ errorList.length }})
          <el-icon
            class="icon"
            @click="drawer = false"
          >
            <Close />
          </el-icon>
        </div>

        <div class="errorList">
          <div
            v-for="(item, index) in errorList"
            :key="`${item.name || 'error'}-${index}`"
            class="item"
          >
            <span class="li" />
            {{ item.name }} : <span style="color: #e1291e">{{ item.failReason }}</span>
          </div>
        </div>
      </el-drawer>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, ref, watch, type ComponentInternalInstance } from 'vue';
import { listByIds } from '../../api/system/oss/index.ts';
import type { OssVO } from '../../api/system/oss/types.ts';
import { propTypes } from '../../utils/propTypes.ts';
import request from '../../utils/request.ts';
import { compressAccurately } from 'image-conversion';

const props = defineProps({
  modelValue: {
    type: [String, Object, Array],
    default: () => [],
  },

  limit: propTypes.number.def(9),
  fileSize: propTypes.number.def(9),
  fileType: propTypes.array.def(['png', 'jpg', 'jpeg', 'gif']),
  isShowTip: {
    type: Boolean,
    default: true,
  },
  compressSupport: {
    type: Boolean,
    default: false,
  },
  categoryId: {
    type: [String, Boolean],
    default: false,
  },
  compressTargetSize: propTypes.number.def(300),
}); // 用于暂存选择的文件
const successCount = ref(0);
const failCount = ref(0);
const errorList = ref([]);
const drawer = ref(false);
const preOpen = ref(false);

const pushindex = ref(1);
const { proxy } = getCurrentInstance() as ComponentInternalInstance;
const emit = defineEmits(['update:modelValue', 'isUpdate']);

const uploadImgUrl = ref('/b/brand/material/addMaterial');
const fileInputRef = ref<HTMLInputElement | null>(null);

const fileList = ref<any[]>([]);
const fileAccept = computed(() => props.fileType.map((type) => `.${type}`).join(','));

watch(
  () => props.modelValue,
  async (val: string) => {
    if (val) {
      let list: OssVO[] = [];
      if (Array.isArray(val)) {
        list = val as OssVO[];
      } else {
        const res = await listByIds(val);
        list = res.data;
      }
      fileList.value = list.map((item) => {
        let itemData;
        if (typeof item === 'string') {
          itemData = { name: item, url: item };
        } else {
          itemData = { name: item.ossId, url: item.url, ossId: item.ossId };
        }
        return itemData;
      });
    } else {
      fileList.value = [];
    }
  },
  { deep: true, immediate: true },
);
const failDetail = () => {
  drawer.value = true;
};

const validateFile = (file: File) => {
  let isImg = false;
  if (props.fileType.length) {
    let fileExtension = '';
    if (file.name.lastIndexOf('.') > -1) {
      fileExtension = file.name.slice(file.name.lastIndexOf('.') + 1);
    }
    isImg = props.fileType.some((type: any) => {
      if (file.type.indexOf(type) > -1) return true;
      if (fileExtension && fileExtension.indexOf(type) > -1) return true;
      return false;
    });
  } else {
    isImg = file.type.indexOf('image') > -1;
  }
  if (!isImg) {
    proxy?.$modal.msgError(`文件格式不正确, 请上传${props.fileType.join('/')}图片格式文件!`);
    return false;
  }
  if (file.name.includes(',')) {
    proxy?.$modal.msgError('文件名不正确，不能包含英文逗号!');
    return false;
  }
  if (props.fileSize) {
    const isLt = file.size / 1024 / 1024 < props.fileSize;
    if (!isLt) {
      proxy?.$modal.msgError(`上传头像图片大小不能超过 ${props.fileSize} MB!`);
      return false;
    }
  }
  return true;
};

const triggerFileInput = () => {
  fileInputRef.value?.click();
};

const handleFileChange = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const files = target.files;
  if (!files || files.length === 0) return;

  if (files.length > props.limit) {
    proxy?.$modal.msgError(`上传文件数量不能超过 ${props.limit} 个!`);
    target.value = '';
    return;
  }

  const validFiles: File[] = [];
  for (let i = 0; i < files.length; i++) {
    if (validateFile(files[i])) {
      validFiles.push(files[i]);
    }
  }

  if (validFiles.length === 0) {
    target.value = '';
    return;
  }

  proxy?.$modal.loading('正在上传图片，请稍候...');

  const compressedFiles: File[] = [];
  for (let i = 0; i < validFiles.length; i++) {
    const file = validFiles[i];
    if (props.compressSupport && file.size / 1024 > props.compressTargetSize) {
      const compressedFile = await compressAccurately(file, props.compressTargetSize);
      compressedFiles.push(compressedFile);
    } else {
      compressedFiles.push(file);
    }
  }

  const formData = new FormData();
  compressedFiles.forEach((file) => {
    formData.append('files', file, file.name);
  });

  try {
    const res = await request({
      url: uploadImgUrl.value,
      method: 'post',
      params: {
        type: 'IMAGE',
        categoryId: props.categoryId,
      },
      data: formData,
    });

    if (res.code === 200) {
      successCount.value = res.data.successCount;
      failCount.value = res.data.failCount;
      errorList.value = res.data.failList;
      preOpen.value = true;

      emit('update:modelValue', []);
      emit('isUpdate');
      proxy?.$modal.closeLoading();
      pushindex.value++;
    } else {
      proxy?.$modal.closeLoading();
      proxy?.$modal.msgError(res.msg);
    }
  } catch {
    proxy?.$modal.closeLoading();
    proxy?.$modal.msgError('上传图片失败');
  }

  target.value = '';
};
</script>

<style lang="scss" scoped>
.file-input {
  display: none;
}

.PreCenter {
  text-align: center;

  .pretip {
    color: #333;
    font-size: 16px;
    margin: 10px 0;
  }

  .perfill {
    font-size: 12px;
    color: #afadad;

    .peritem {
      margin-inline-end: 10px;

      &.active {
        color: var(--color-1);
        cursor: pointer;
      }

      .successCount {
        color: #00c261;
      }

      .failCount {
        color: #e1291e;
      }
    }
  }
}

.title {
  height: 30px;
  border-bottom: 1px #eeeeee solid;
  font-weight: 600;
  position: relative;

  .icon {
    position: absolute;
    inset-inline-end: 10px;
    font-size: 20px;
  }
}

.errorList {
  height: calc(100vh - 66px);
  overflow-y: auto;
  padding: 10px 0;

  .item {
    margin-bottom: 10px;

    .li {
      display: inline-block;
      width: 6px;
      height: 6px;
      background: rgb(235, 129, 129);
      border-radius: 3px;
    }
  }
}
</style>
